import { useRef, useState } from 'react';
import { Box, Button, Container, Grid, Link, Stack, Typography, useTheme } from '@mui/material';
import OCIDImage from '../../assets/marketing/ocid.png'
import UsAgencyStatistic from '../../assets/marketing/us_agency_statistic.png';
import Officers from '../../assets/marketing/officers.png'
// import GiftCard from '../../assets/marketing/gift_card.png';
import LogotTypeLight from '../../assets/surePassLogoHoriztonalColor.svg';
import RegistrationForm from './components/RegistrationForm';
import Success from './components/Success';

const PublicRegistrationPage: React.FC = () => {
  const theme = useTheme();
  const formContainerRef = useRef<HTMLDivElement | null>(null);

  const [successActive, setSuccessActive] = useState<boolean>(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const RegisterButton = ({ gutterTop }: { gutterTop?: 'sm' | 'lg' }) => (
    <Button
      variant='contained'
      sx={[styles.button, { marginTop: gutterTop === 'lg' ? '24px' : '16px' }]}
      onClick={() => {
        formContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
      }}
    >
      Register Now
    </Button>
  );

  const styles = {
    textLight: {
      color: '#FFF',
    },
    textBold: {
      fontWeight: 700,
    },
    alignLeft: {
      textAlign: 'left',
    },
    alignCenter: {
      textAlign: 'center',
    },
    alignCenterSmall: {
      [theme.breakpoints.down('md')]: {
        textAlign: 'center',
      },
    },
    alignLeftSmall: {
      [theme.breakpoints.down('md')]: {
        textAlign: 'left',
      },
    },
    gutterTopSmall: {
      [theme.breakpoints.down('sm')]: {
        marginTop: '8px',
      },
    },
    gutterTopMedium: {
      [theme.breakpoints.up('sm')]: {
        marginTop: '8px',
      },
    },
    h1: {
      fontWeight: 700,
      fontSize: '48px',
      lineHeight: '140%',
      color: theme.palette.text.primary,
    },
    h1Small: {
      fontWeight: 700,
      fontSize: '34px',
      lineHeight: '120%',
      color: theme.palette.text.primary,
    },
    h2: {
      fontWeight: 500,
      fontSize: '32px',
      lineHeight: '140%',
      color: theme.palette.text.primary,
      [theme.breakpoints.down('md')]: {
        fontSize: '20px',
      },
    },
    h3: {
      fontWeight: 400,
      fontSize: '24px',
      lineHeight: '140%',
      color: theme.palette.text.primary,
    },
    bodyLarge: {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '140%',
      color: theme.palette.text.primary,
      [theme.breakpoints.down('sm')]: {
        textAlign: 'center',
      },
    },
    bodySmall: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '140%',
      color: theme.palette.text.primary,
      [theme.breakpoints.down('sm')]: {
        textAlign: 'center',
      },
    },
    button: {
      borderRadius: '10px',
      background: 'linear-gradient(to right, #66DAFD, #00FAC2)',
      color: theme.palette.text.primary,
      fontWeight: 600,
      fontSize: '16px',
      textTransform: 'none',
    },
    higlight: {
      backgroundColor: theme.palette.teal[200],
      width: 'fit-content',
      paddingRight: '8px',
      paddingLeft: '8px',
    },
    hideOnSmall: {
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    hideOnLarge: {
      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
    },
    header: {
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'center',
      },
    },
    carryWithConfidenceSection: {
      paddingTop: '96px',
      paddingBottom: '96px',
      [theme.breakpoints.down('sm')]: {
        paddingTop: '40px',
        paddingBottom: '32px',
      },
    },
    whatIsSurePass: {
      backgroundColor: theme.palette.action.selected,
      paddingTop: '72px',
      paddingBottom: '72px',
      [theme.breakpoints.down('sm')]: {
        paddingTop: '48px',
        paddingBottom: '48px',
      },
    },
    howItWorks: {
      backgroundColor: theme.palette.text.primary,
      paddingTop: '72px',
      paddingBottom: '72px',
      [theme.breakpoints.down('sm')]: {
        paddingTop: '48px',
        paddingBottom: '48px',
      },
    },
    numberBulletCircle: {
      width: 25,
      height: 25,
      borderRadius: '50%',
      backgroundColor: theme.palette.teal[200],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginRight: '12px',
    },
    numberBulletText: {
      fontWeight: 600,
    },
    builtByLawEnforcement: {
      paddingTop: '72px',
      paddingBottom: '96px',
      [theme.breakpoints.down('sm')]: {
        paddingTop: '48px',
        paddingBottom: '48px',
      },
    },
    incentive: {
      backgroundColor: theme.palette.text.primary,
      paddingTop: '48px',
      paddingBottom: '48px',
    },
    formSection: {
      paddingTop: '64px',
      paddingBottom: '64px',
      backgroundColor: theme.palette.action.selected,
      width: '100%',
    },
    img: {
      width: '100%',
      display: 'block',
      height: 'auto',
      maxWidth: '340px',
    },
  };

  const centerForSmall = { xs: 'center', md: 'flex-start', lg: 'flex-start', xl: 'flex-start' };

  return (
    <Box>
      <Stack
        component='header'
        direction='row'
        sx={[{
          p: '16px'
        }, styles.header]}>
        <Link href='/'>
          <Box component='img' alt='SurePass logo' src={LogotTypeLight} style={{ width: '159px', height: 'auto' }} />
        </Link>
      </Stack>
      <main>
        <Container maxWidth='lg' sx={styles.carryWithConfidenceSection}>
          <Grid container direction='row' columnSpacing={2}>
            <Grid container size={{ xs: 12, md: 6 }} sx={{
              flexDirection: 'column',
              alignItems: centerForSmall
            }}>
              <Box sx={[styles.higlight, styles.hideOnSmall]}>
                <Typography sx={styles.h1} component='h1'>Carry with Confidence</Typography>
              </Box>
              <Box sx={[styles.higlight, styles.hideOnLarge]}>
                <Typography sx={styles.h1Small}>Carry with</Typography>
              </Box>
              <Box
                sx={[{
                  mt: '8px'
                }, styles.higlight, styles.hideOnLarge]}>
                <Typography sx={styles.h1Small}>Confidence</Typography>
              </Box>
              <Grid
                container
                sx={{
                  flexDirection: 'column',
                  alignItems: centerForSmall,

                  [theme.breakpoints.up('md')]: {
                    paddingLeft: '8px'
                  }
                }}>
                <Typography sx={[styles.h3, styles.alignCenterSmall, styles.gutterTopSmall, styles.gutterTopMedium]}>Use Your Authority Privileges Without Delay</Typography>
                <RegisterButton gutterTop='lg' />
                <Typography
                  sx={[{
                    mt: '24px'
                  }, styles.bodyLarge]}>
                  When carrying their firearm, officers are often stopped or delayed at public and private facilities.
                </Typography>
                <Typography
                  sx={[{
                    mt: '16px'
                  }, styles.bodyLarge]}>
                  SurePass gives you a <Typography component='span' sx={styles.textBold}>fast, professional way to prove your Official Capacity and LEOSA Authority</Typography> so you can enter secure environments without friction.
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              size={{ xs: 12, md: 6 }}
              sx={{ flexDirection: 'column', [theme.breakpoints.down('sm')]: { marginTop: '40px' } }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box component='img' alt='Official Capacity ID (OCID)' src={OCIDImage} sx={styles.img} />
              </Box>
            </Grid>
          </Grid>
        </Container>
        <Box sx={styles.whatIsSurePass}>
          <Container maxWidth='lg'>
            <Grid container direction='row' columnSpacing={1} sx={{
              alignItems: 'center'
            }}>
              <Grid
                container
                size={{ xs: 12, md: 6 }}
                sx={{
                  flexDirection: 'column',
                  alignItems: 'center',

                  [theme.breakpoints.down('sm')]: {
                    order: 1,
                    marginTop: '24px',
                  }
                }}>
                <Box component='img' alt='850+ U.S. Agencies' src={UsAgencyStatistic} sx={styles.img} />
                <Typography
                  align='center'
                  sx={[{
                    mt: '16px'
                  }, styles.bodySmall]}>
                  SurePass is in use across major U.S.
                </Typography>
                <Typography
                  align='center'
                  sx={[{
                    mt: '4px'
                  }, styles.bodySmall]}>
                  law enforcement agencies.
                </Typography>
              </Grid>
              <Grid container size={{ md: 5, sm: 12 }} sx={{ flexDirection: 'column' }}>
                <Typography sx={[styles.h2, styles.alignCenterSmall]}>
                  What is <Typography component='span' sx={[styles.h2, styles.textBold]}>SurePass?</Typography>
                </Typography>
                <Typography
                  sx={[{
                    mt: '16px'
                  }, styles.alignLeftSmall]}>
                  SurePass gives you a secure digital credential (Official Capacity ID) that allows you to:
                </Typography>
                <ul style={{ marginTop: '16px', marginLeft: '20px' }}>
                  <li>
                    <Typography sx={styles.alignLeftSmall}>
                      <Typography component='span' sx={[styles.alignLeftSmall, styles.textBold]}>Show</Typography> your law enforcement status instantly
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.alignLeftSmall}>
                      <Typography component='span' sx={[styles.alignLeftSmall, styles.textBold]}>Confirm</Typography> LEOSA carry eligibility
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.alignLeftSmall}>
                      <Typography component='span' sx={[styles.alignLeftSmall, styles.textBold]}>Avoid</Typography> unnecessary confrontation or denial of entry
                    </Typography>
                  </li>
                </ul>
              </Grid>
            </Grid>
          </Container>
        </Box>
        <Box sx={styles.howItWorks}>
          <Container maxWidth='sm'>
            <Stack direction='column' sx={{
              alignItems: 'center'
            }}>
              <Typography sx={[styles.h2, styles.textLight]}>How It Works</Typography>
              <Stack direction='column' sx={{
                alignItems: 'flex-start'
              }}>
                <Stack direction='row' sx={{
                  mt: '16px'
                }}>
                  <Box sx={styles.numberBulletCircle}>
                    <Typography sx={[styles.bodySmall, styles.numberBulletText]}>1</Typography>
                  </Box>
                  <Typography sx={[styles.alignLeftSmall, styles.textLight]}>
                    <Typography component='span' sx={{ fontWeight: 700 }}>Register (in under 2 minutes).</Typography> Enter basic professional info (name, agency, badge, official email)
                  </Typography>
                </Stack>
                <Stack direction='row' sx={{
                  mt: '16px'
                }}>
                  <Box sx={styles.numberBulletCircle}>
                    <Typography sx={[styles.bodySmall, styles.numberBulletText]}>2</Typography>
                  </Box>
                  <Typography sx={[styles.alignLeftSmall, styles.textLight]}>
                    <Typography component='span' sx={{ fontWeight: 700 }}>Get Verified.</Typography> Status confirmed through secure law enforcement sources
                  </Typography>
                </Stack>
                <Stack direction='row' sx={{
                  mt: '16px'
                }}>
                  <Box sx={styles.numberBulletCircle}>
                    <Typography sx={[styles.bodySmall, styles.numberBulletText]}>3</Typography>
                  </Box>
                  <Typography sx={[styles.alignLeftSmall, styles.textLight]}>
                    <Typography component='span' sx={{ fontWeight: 700 }}>Use Your OCID.</Typography> Present your mobile credential at participating checkpoints when needed.
                  </Typography>
                </Stack>
              </Stack>
              <RegisterButton />
            </Stack>
          </Container>
        </Box>
        <Container maxWidth='lg' sx={styles.builtByLawEnforcement}>
          <Grid container columnSpacing={1} sx={{
            alignItems: 'center'
          }}>
            <Grid
              container
              size={{ xs: 12, md: 6 }}
              sx={[{
                justifyContent: 'center'
              }, styles.hideOnSmall]}>
              <Box component='img' alt='Verified Law Enforcement Officers' src={Officers} sx={styles.img} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction='column' sx={{
                alignItems: 'center'
              }}>
                <Stack
                  direction='column'
                  sx={[{
                    alignItems: 'center'
                  }, styles.hideOnSmall]}>
                  <Typography sx={styles.h2}>SurePass is Built</Typography>
                  <Typography sx={styles.h2}>By and For</Typography>
                  <Box sx={styles.higlight}>
                    <Typography sx={styles.h2}>Law Enforcement</Typography>
                  </Box>
                </Stack>
                <Typography align='center' sx={[styles.h2, styles.hideOnLarge]}>
                  SurePass is
                </Typography>
                <Typography align='center' sx={[styles.h2, styles.hideOnLarge, { marginLeft: '60px', marginRight: '60px' }]}>
                  Built <Typography sx={[styles.h2, styles.textBold]} component='span'>By Law Enforcement,</Typography>
                </Typography>
                <Typography sx={[styles.h2, styles.textBold, styles.hideOnLarge]} component='span'>For Law Enforcement</Typography>
                <Grid
                  container
                  size={{ xs: 12, md: 6 }}
                  sx={[{
                    justifyContent: 'center'
                  }, styles.hideOnLarge]}>
                  <Box component='img' src={Officers} sx={{ width: '100%' }} />
                </Grid>
                <Typography
                  sx={[{
                    mt: '16px'
                  }, styles.bodySmall, styles.textBold, styles.alignLeftSmall]}>
                  SurePass captures only standard professional credential information.
                </Typography>
                <Typography
                  sx={[{
                    mt: '16px'
                  }, styles.bodySmall, styles.alignLeftSmall]}>
                  No unnecessary personal data is collected, and your information is not shared. Our system is designed to verify authority, not track officers.
                </Typography>
                <RegisterButton />
                <Typography sx={{
                  mt: '16px'
                }}>Don’t see your agency?
                  <Link sx={{ color: theme.palette.text.primary, fontWeight: 700, textDecoration: 'none' }}> Contact us.</Link>
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Container>
        {/* <Box sx={styles.incentive}>
          <Container maxWidth='lg'>
            <Grid container columnSpacing={1} sx={{ alignItems: 'center' }}>
              <Grid container size={{ xs: 12, md: 6 }} sx={{
                justifyContent: 'center',
                [theme.breakpoints.down('sm')]: {
                  order: 2,
                  marginTop: '8px'
                },
              }}>
                <Box component='img' src={GiftCard} sx={styles.img} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} sx={{
                [theme.breakpoints.down('sm')]: {
                  order: 1
                },
              }}>
                <Stack direction='column' sx={{ alignItems: 'center' }}>
                  <Typography sx={[styles.h2, styles.textLight]}>
                    Limited-Time Incentive
                  </Typography>
                  <Typography align='center' sx={[{ mt: '16px' }, styles.bodySmall, styles.textLight]}>
                    Receive a $25 electronic gift card at the following Colorado retailers. Select which card you would like to receive.
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box> */}
        <Box sx={styles.formSection}>

          {
            successActive
              ? (
                <Container maxWidth='md'>
                  <Success email={registeredEmail} />
                </Container>
              )
              : (
                <Container ref={formContainerRef} maxWidth='sm'>
                  <RegistrationForm promoName='CO_SPRING_2026' onSuccess={(email) => {
                    setRegisteredEmail(email);
                    setSuccessActive(true);
                  }} />
                </Container>
              )
          }

        </Box>

      </main>
    </Box>
  );
};

export default PublicRegistrationPage;
